// Branding settings page.
// T3 config screen — tenant brand colours, font, and logo.

import { useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useApi, ApiRequestError } from '@/lib/api/client'
import { useTenant } from '@/components/shared/TenantShell'
import {
  brandingApi,
  SUPPORTED_FONTS,
  type BrandingColors,
  type UpdateBrandingRequest,
} from '@/lib/api/branding'
import { queryKeys } from '@/lib/api/queryKeys'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card'
import { ErrorState } from '@/components/shared/ErrorState'
import { FormSkeleton } from '@/components/shared/LoadingSkeleton'
import { useToast } from '@/lib/hooks/use-toast'
import { Loader2, Upload, Trash2, RotateCcw } from 'lucide-react'

/** Convert HSL string "210 50% 20%" to hex for the colour input. */
function hslToHex(hsl: string): string {
  const parts = hsl.match(/[\d.]+/g)
  if (!parts || parts.length < 3) return '#888888'

  const h = parseFloat(parts[0]!) / 360
  const s = parseFloat(parts[1]!) / 100
  const l = parseFloat(parts[2]!) / 100

  function hue2rgb(p: number, q: number, t: number): number {
    if (t < 0) t += 1
    if (t > 1) t -= 1
    if (t < 1 / 6) return p + (q - p) * 6 * t
    if (t < 1 / 2) return q
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6
    return p
  }

  let r: number, g: number, b: number
  if (s === 0) {
    r = g = b = l
  } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s
    const p = 2 * l - q
    r = hue2rgb(p, q, h + 1 / 3)
    g = hue2rgb(p, q, h)
    b = hue2rgb(p, q, h - 1 / 3)
  }

  const toHex = (n: number) =>
    Math.round(n * 255)
      .toString(16)
      .padStart(2, '0')

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`
}

/** Convert hex "#aabbcc" to HSL string "210 50% 20%". */
function hexToHsl(hex: string): string {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
  if (!result) return '0 0% 50%'

  const r = parseInt(result[1]!, 16) / 255
  const g = parseInt(result[2]!, 16) / 255
  const b = parseInt(result[3]!, 16) / 255

  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  let h = 0
  let s = 0
  const l = (max + min) / 2

  if (max !== min) {
    const d = max - min
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6
        break
      case g:
        h = ((b - r) / d + 2) / 6
        break
      case b:
        h = ((r - g) / d + 4) / 6
        break
    }
  }

  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`
}

/** Platform defaults — must match index.css :root values. */
const DEFAULTS: BrandingColors = {
  headerBackground: '220 15% 33%',
  headerForeground: '220 15% 92%',
  sidebarBackground: '220 13% 91%',
  sidebarForeground: '220 20% 25%',
  footerBackground: '220 15% 33%',
  footerForeground: '220 15% 92%',
  background: '220 20% 97%',
  foreground: '222.2 84% 4.9%',
  bodyForeground: '220 13% 42%',
  border: '220 13% 85%',
  card: '214 50% 96%',
  cardForeground: '222.2 84% 4.9%',
  cardHeaderForeground: '222.2 84% 4.9%',
  buttonBackground: '217 45% 45%',
  buttonForeground: '210 40% 98%',
  gridAppetiteIn: '120 40% 85%',
  gridAppetiteOut: '0 50% 85%',
  gridNodeInherent: '30 80% 55%',
  gridNodeResidual: '217 70% 50%',
  gridNodeTarget: '150 60% 40%',
}

interface ColorFieldProps {
  label: string
  colorKey: keyof BrandingColors
  colors: BrandingColors
  onChange: (key: keyof BrandingColors, value: string) => void
}

function ColorField({ label, colorKey, colors, onChange }: ColorFieldProps) {
  const hslValue = colors[colorKey] ?? DEFAULTS[colorKey] ?? '0 0% 50%'
  const hexValue = hslToHex(hslValue)

  return (
    <div className="flex items-center gap-3">
      <input
        type="color"
        value={hexValue}
        onChange={(e) => onChange(colorKey, hexToHsl(e.target.value))}
        className="h-9 w-12 cursor-pointer rounded border p-0.5"
      />
      <Label className="text-sm">{label}</Label>
    </div>
  )
}

export function BrandingPage() {
  const { t } = useTranslation(['settings', 'common'])
  const api = useApi()
  const { slug, tenantId } = useTenant()
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const bApi = brandingApi(api)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Local state for form edits
  const [colors, setColors] = useState<BrandingColors>({})
  const [fontId, setFontId] = useState('inter')
  const [isDirty, setIsDirty] = useState(false)

  const {
    data: branding,
    error,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: queryKeys.tenantSettings.branding(slug),
    queryFn: () => bApi.get(),
    retry: false,
  })

  // Sync state when data loads
  const [initialised, setInitialised] = useState(false)
  if (branding && !initialised) {
    setColors(branding.colors ?? {})
    setFontId(branding.fontId ?? 'inter')
    setInitialised(true)
  }

  function handleColorChange(key: keyof BrandingColors, value: string): void {
    setColors((prev) => ({ ...prev, [key]: value }))
    setIsDirty(true)
  }

  // Save branding config
  const saveMutation = useMutation({
    mutationFn: (data: UpdateBrandingRequest) => bApi.update(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tenantSettings.branding(slug) })
      setIsDirty(false)
      toast({ title: t('branding.updated') })
    },
    onError: (err) => {
      toast({
        title: t('branding.updateFailed'),
        description: err instanceof ApiRequestError ? err.message : t('common:errors.generic'),
        variant: 'destructive',
      })
    },
  })

  function handleSave(): void {
    saveMutation.mutate({ colors, fontId })
  }

  // Reset to defaults
  function handleReset(): void {
    setColors({})
    setFontId('inter')
    saveMutation.mutate({ colors: {}, fontId: 'inter' })
  }

  // Logo upload
  const uploadMutation = useMutation({
    mutationFn: (file: File) => bApi.uploadLogo(file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tenantSettings.branding(slug) })
      toast({ title: t('branding.logo.uploadSuccess') })
    },
    onError: () => {
      toast({ title: t('branding.logo.uploadFailed'), variant: 'destructive' })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: () => bApi.deleteLogo(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tenantSettings.branding(slug) })
      toast({ title: t('branding.logo.removeSuccess') })
    },
    onError: () => {
      toast({ title: t('branding.logo.removeFailed'), variant: 'destructive' })
    },
  })

  function handleFileSelect(e: React.ChangeEvent<HTMLInputElement>): void {
    const file = e.target.files?.[0]
    if (!file) return

    if (file.type !== 'image/png') {
      toast({ title: t('branding.logo.invalidFormat'), variant: 'destructive' })
      return
    }
    if (file.size > 204800) {
      toast({ title: t('branding.logo.tooLarge'), variant: 'destructive' })
      return
    }

    uploadMutation.mutate(file)
    // Reset the input so the same file can be re-selected
    e.target.value = ''
  }

  if (isLoading) {
    return <FormSkeleton />
  }

  if (error) {
    return (
      <ErrorState
        message={t('common:errors.loadFailed', { resource: t('branding.title') })}
        technicalDetail={error instanceof ApiRequestError ? error.message : undefined}
        onRetry={() => refetch()}
      />
    )
  }

  const logoUrl = branding?.hasLogo
    ? `/api/v1/tenants/${tenantId}/branding/logo?t=${Date.now()}`
    : undefined

  return (
    <div className="max-w-2xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold">{t('branding.title')}</h1>
        <p className="text-muted-foreground">{t('branding.description')}</p>
      </div>

      {/* Logo */}
      <Card>
        <CardHeader>
          <CardTitle>{t('branding.logo.title')}</CardTitle>
          <CardDescription>{t('branding.logo.description')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {logoUrl && (
            <div className="rounded-md border bg-muted/30 p-4">
              <img src={logoUrl} alt="Current logo" className="max-h-[76px] w-auto" />
            </div>
          )}
          <div className="flex gap-2">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png"
              className="hidden"
              onChange={handleFileSelect}
            />
            <Button
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadMutation.isPending}
            >
              {uploadMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Upload className="mr-2 h-4 w-4" />
              )}
              {logoUrl ? t('branding.logo.replace') : t('branding.logo.upload')}
            </Button>
            {logoUrl && (
              <Button
                variant="outline"
                onClick={() => deleteMutation.mutate()}
                disabled={deleteMutation.isPending}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                {t('branding.logo.remove')}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Font */}
      <Card>
        <CardHeader>
          <CardTitle>{t('branding.font.title')}</CardTitle>
          <CardDescription>{t('branding.font.description')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {SUPPORTED_FONTS.map((font) => (
              <button
                key={font.id}
                onClick={() => {
                  setFontId(font.id)
                  setIsDirty(true)
                }}
                className={`rounded-md border p-3 text-left transition-colors ${
                  fontId === font.id
                    ? 'border-primary bg-primary/5 ring-1 ring-primary'
                    : 'hover:border-muted-foreground/30'
                }`}
              >
                <p className="text-sm font-medium">{font.name}</p>
                <p className="text-xs text-muted-foreground">{font.category}</p>
                <p className="mt-1 text-sm" style={{ fontFamily: font.family }}>
                  {t('branding.font.preview')}
                </p>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Colours */}
      <Card>
        <CardHeader>
          <CardTitle>{t('branding.colors.title')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Header */}
          <div>
            <h4 className="mb-3 text-sm font-medium">{t('branding.colors.header')}</h4>
            <div className="grid grid-cols-2 gap-4">
              <ColorField
                label={t('branding.colors.background')}
                colorKey="headerBackground"
                colors={colors}
                onChange={handleColorChange}
              />
              <ColorField
                label={t('branding.colors.text')}
                colorKey="headerForeground"
                colors={colors}
                onChange={handleColorChange}
              />
            </div>
          </div>

          {/* Sidebar */}
          <div>
            <h4 className="mb-3 text-sm font-medium">{t('branding.colors.sidebar')}</h4>
            <div className="grid grid-cols-2 gap-4">
              <ColorField
                label={t('branding.colors.background')}
                colorKey="sidebarBackground"
                colors={colors}
                onChange={handleColorChange}
              />
              <ColorField
                label={t('branding.colors.text')}
                colorKey="sidebarForeground"
                colors={colors}
                onChange={handleColorChange}
              />
            </div>
          </div>

          {/* Footer */}
          <div>
            <h4 className="mb-3 text-sm font-medium">{t('branding.colors.footer')}</h4>
            <div className="grid grid-cols-2 gap-4">
              <ColorField
                label={t('branding.colors.background')}
                colorKey="footerBackground"
                colors={colors}
                onChange={handleColorChange}
              />
              <ColorField
                label={t('branding.colors.text')}
                colorKey="footerForeground"
                colors={colors}
                onChange={handleColorChange}
              />
            </div>
          </div>

          {/* Main Content */}
          <div>
            <h4 className="mb-3 text-sm font-medium">{t('branding.colors.mainContent')}</h4>
            <div className="grid grid-cols-2 gap-4">
              <ColorField
                label={t('branding.colors.background')}
                colorKey="background"
                colors={colors}
                onChange={handleColorChange}
              />
              <ColorField
                label={t('branding.colors.headingText')}
                colorKey="foreground"
                colors={colors}
                onChange={handleColorChange}
              />
              <ColorField
                label={t('branding.colors.bodyText')}
                colorKey="bodyForeground"
                colors={colors}
                onChange={handleColorChange}
              />
              <ColorField
                label={t('branding.colors.borders')}
                colorKey="border"
                colors={colors}
                onChange={handleColorChange}
              />
            </div>
          </div>

          {/* Cards */}
          <div>
            <h4 className="mb-3 text-sm font-medium">{t('branding.colors.cards')}</h4>
            <div className="grid grid-cols-2 gap-4">
              <ColorField
                label={t('branding.colors.background')}
                colorKey="card"
                colors={colors}
                onChange={handleColorChange}
              />
              <ColorField
                label={t('branding.colors.text')}
                colorKey="cardForeground"
                colors={colors}
                onChange={handleColorChange}
              />
              <ColorField
                label={t('branding.colors.cardHeaderText')}
                colorKey="cardHeaderForeground"
                colors={colors}
                onChange={handleColorChange}
              />
            </div>
          </div>

          {/* Buttons */}
          <div>
            <h4 className="mb-3 text-sm font-medium">{t('branding.colors.buttons')}</h4>
            <div className="grid grid-cols-2 gap-4">
              <ColorField
                label={t('branding.colors.background')}
                colorKey="buttonBackground"
                colors={colors}
                onChange={handleColorChange}
              />
              <ColorField
                label={t('branding.colors.text')}
                colorKey="buttonForeground"
                colors={colors}
                onChange={handleColorChange}
              />
            </div>
          </div>

          {/* Risk Grid */}
          <div>
            <h4 className="mb-3 text-sm font-medium">{t('branding.colors.riskGrid')}</h4>
            <div className="grid grid-cols-2 gap-4">
              <ColorField
                label={t('branding.colors.inAppetite')}
                colorKey="gridAppetiteIn"
                colors={colors}
                onChange={handleColorChange}
              />
              <ColorField
                label={t('branding.colors.outOfAppetite')}
                colorKey="gridAppetiteOut"
                colors={colors}
                onChange={handleColorChange}
              />
            </div>
          </div>

          {/* Risk Nodes */}
          <div>
            <h4 className="mb-3 text-sm font-medium">{t('branding.colors.riskNodes')}</h4>
            <div className="grid grid-cols-2 gap-4">
              <ColorField
                label={t('branding.colors.inherentNode')}
                colorKey="gridNodeInherent"
                colors={colors}
                onChange={handleColorChange}
              />
              <ColorField
                label={t('branding.colors.residualNode')}
                colorKey="gridNodeResidual"
                colors={colors}
                onChange={handleColorChange}
              />
              <ColorField
                label={t('branding.colors.targetNode')}
                colorKey="gridNodeTarget"
                colors={colors}
                onChange={handleColorChange}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex items-center gap-4">
        <Button onClick={handleSave} disabled={!isDirty || saveMutation.isPending}>
          {saveMutation.isPending ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {t('common:saving')}
            </>
          ) : (
            t('common:save')
          )}
        </Button>
        <Button variant="outline" onClick={handleReset} disabled={saveMutation.isPending}>
          <RotateCcw className="mr-2 h-4 w-4" />
          {t('branding.resetAll')}
        </Button>
      </div>
    </div>
  )
}
